// Turbo's confirmations, in the product's own dialog (P11.10).
//
// 29 places pass `data-turbo-confirm` and every one of them was drawing the
// BROWSER's `confirm()`: a grey slab titled "varaexpo.novas.com.mv says",
// unstyleable, and announcing the host name to a member of staff who is
// deleting a photograph. `shared/_dialog.html.erb` has been the approved
// pattern since P2.4 and Turbo simply was not told to use it — there is no
// `setConfirmMethod` anywhere in the app.
//
// One override rather than 29 edits. Every existing `turbo_confirm:` string
// keeps working untouched.
//
// The two rules from DESIGN.md and docs/06 that the native dialog cannot
// honour, and this does:
//   - it names the record AND the consequence, which the messages already do;
//   - CANCEL TAKES FOCUS, not the destructive button, so Enter on a dialog
//     somebody opened by accident does nothing.
import { Turbo } from "@hotwired/turbo-rails"

// The messages are written as "Delete The 2025 floor? This cannot be undone."
// — a question, then its consequence. Splitting on the first question mark
// gives the dialog the heading and body it wants without rewriting any of
// them. A message with no question mark becomes the heading alone, which is
// what "Archive Vara Expo 2025?" should look like.
function split(message) {
  const at = message.indexOf("?")
  if (at === -1) return [ message, "" ]

  return [ message.slice(0, at + 1), message.slice(at + 1).trim() ]
}

// Destructive by wording, because that is the only signal available: Turbo
// hands over a string and the element, and nothing tells us the intent. These
// four verbs cover every confirmation in the app and a new one that does not
// match gets the neutral treatment, which is the safe way round.
const DESTRUCTIVE = /^(delete|remove|cancel|archive|release)\b/i

function build() {
  const dialog = document.createElement("dialog")
  dialog.id = "turbo-confirm"
  dialog.className =
    "m-auto w-[min(32rem,calc(100vw-2rem))] rounded-lg border border-line bg-surface p-0 text-ink shadow-overlay backdrop:bg-ink/30"
  dialog.innerHTML = `
    <div class="p-5">
      <h2 class="text-h3" data-confirm-title></h2>
      <p class="mt-2 text-ink-secondary" data-confirm-body hidden></p>
      <div class="mt-5 flex justify-end gap-2">
        <button type="button" value="cancel" data-confirm-cancel></button>
        <button type="button" value="confirm" data-confirm-accept></button>
      </div>
    </div>`
  document.body.appendChild(dialog)
  return dialog
}

Turbo.setConfirmMethod((message, element) => {
  const dialog = document.getElementById("turbo-confirm") ?? build()
  const [ title, body ] = split(message)
  const destructive = DESTRUCTIVE.test(title)

  dialog.querySelector("[data-confirm-title]").textContent = title

  const bodyEl = dialog.querySelector("[data-confirm-body]")
  bodyEl.textContent = body
  bodyEl.hidden = body === ""

  // The button classes come from the same semantic utilities the rest of the
  // console uses, so a theme change moves this with everything else.
  const cancel = dialog.querySelector("[data-confirm-cancel]")
  cancel.textContent = element?.dataset?.turboConfirmCancel ?? "Cancel"
  cancel.className =
    "inline-flex h-9 items-center justify-center rounded-md border border-line-strong bg-surface px-4 text-dense font-medium text-ink"

  const accept = dialog.querySelector("[data-confirm-accept]")
  accept.textContent = element?.dataset?.turboConfirmLabel ?? (destructive ? "Delete" : "Continue")
  accept.className = destructive
    ? "inline-flex h-9 items-center justify-center rounded-md bg-destructive-text px-4 text-dense font-medium text-white"
    : "inline-flex h-9 items-center justify-center rounded-md bg-ink px-4 text-dense font-medium text-surface"

  return new Promise((resolve) => {
    // `once`, so a dialog reused across many confirmations does not
    // accumulate handlers and resolve an old promise with a new answer.
    const settle = (answer) => {
      dialog.close()
      resolve(answer)
    }

    cancel.addEventListener("click", () => settle(false), { once: true })
    accept.addEventListener("click", () => settle(true), { once: true })
    // Escape and the backdrop both close the dialog without a click, and a
    // promise nobody settles leaves the page frozen behind an invisible modal.
    dialog.addEventListener("cancel", () => resolve(false), { once: true })

    dialog.showModal()
    cancel.focus()
  })
})
